/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Box,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthenticationInfoRes,
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '@Types/provision';
import { ProvisionApi, PocApi } from '@Apis';
import { deleteCookie, getProvisionCookie } from '../../../utils/common';
import ErrorAlert from '@Components/Common/ErrorAlert';
import {
  DataSyncFailed,
  DataSynced,
  DataSyncing,
  EnablePolicyRadioGroup,
  ProvisionOrgInfo,
  UserIcon,
  StepIcon,
} from './share';
import { reportPendo } from '@Utils/setupPendo';
import RejectProvisionModal from './rejectProvisionModal';
import { Steps, type StepProps } from 'antd';
import { PocTransFer } from './PocTransFer';
import LoadingBtn from '../../../components/Common/LoadingBtn';
import { App, Service } from '@Types/app';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';
import config from '../../../config';

enum ExchangeStatus {
  NotProvision = 0,
  WaitSelectUser = 1,
  Provisioning = 2,
  Provisioned = 3,
}

const SERVICE = Service.Exchange;

enum IdSyncMode {
  Full = 'full',
  Poc = 'poc',
}

const ExchangeOnlineModal = ({
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: ProvisionModalState) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['provision', 'common']);
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const exchangePocUserLimit = useMemo(() => {
    return (
      (selectedOrgGlobalSetting.exchangePocUserLimit as number) ??
      config.ui.globalSettings.limitation.exchangePocUserLimit
    );
  }, [selectedOrgGlobalSetting]);

  const StatusMap = {
    [ProvisionStatusEnum.NotStarted]: ExchangeStatus.NotProvision,
    [ProvisionStatusEnum.Provisioning]: ExchangeStatus.Provisioning,
    [ProvisionStatusEnum.Provisioned]: ExchangeStatus.Provisioned,

    // just for exchange poc
    [ProvisionStatusEnum.TokenCreated]: ExchangeStatus.WaitSelectUser,
  };
  const [idSyncMode, setIdSyncMode] = useState<IdSyncMode>(
    provisionStatus.isPocProvision === true ? IdSyncMode.Poc : IdSyncMode.Full
  );
  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [exchangeStatus, setExchangeStatus] = useState<ExchangeStatus>(
    // StatusMap[provisionStatus.status] || ExchangeStatus.NotProvision
    () => {
      if (Object.keys(StatusMap).includes(provisionStatus.status)) {
        return StatusMap[provisionStatus.status as keyof typeof StatusMap];
      } else {
        //provisionStatus.status = failure
        //token failure
        if (provisionStatus.authorization.status === TokenStatus.Failure) {
          return ExchangeStatus.NotProvision;
        } else {
          //id sync fail
          return ExchangeStatus.Provisioning;
          //TODO: 同时设置 id sync 为失败的情况
        }
      }
    }
  );
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const pocUsersRef = useRef<{
    //PocUsers
    data: object[];
  }>({
    data: [],
  });

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  useEffect(() => {
    if (exchangeStatus === ExchangeStatus.NotProvision) {
      ProvisionApi.apiGetAuthenticationInfo(SERVICE, orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          console.error(JSON.stringify(e));
          const errorCode = e.data?.error?.details?.[0].errorCode;
          if (
            errorCode === RejectProvisionReason.ServiceConflict ||
            errorCode === RejectProvisionReason.PocConflict
          ) {
            setRejectReason(errorCode);
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={SERVICE}
        reason={rejectReason}
      />
    );
  }

  if (exchangeStatus === ExchangeStatus.NotProvision && !authenticationInfo) {
    return <></>;
  }

  const stepItems: StepProps[] = [
    {
      icon: <UserIcon />,
      title: (
        <Text color={colorStyle.color.primary}>
          {t(
            'provision:provision.service.exchange.poc.synchronizationModeDesc'
          )}
        </Text>
      ),
      description: (
        <>
          <IdSyncModeMenu
            disabled={exchangeStatus !== ExchangeStatus.NotProvision}
            idSyncMode={idSyncMode}
            setIdSyncMode={setIdSyncMode}
          />
          {idSyncMode === IdSyncMode.Poc && (
            <Text color={colorStyle.color.tertiary} pt="16px">
              {t('provision.service.exchange.poc.tip')}
            </Text>
          )}
        </>
      ),
    },
    {
      icon: (
        <StepIcon checked={exchangeStatus !== ExchangeStatus.NotProvision} />
      ),
      title: (
        <Text color={colorStyle.color.primary}>
          {t('provision:provision.service.exchange.grantPermission')}
        </Text>
      ),
      description:
        exchangeStatus !== ExchangeStatus.NotProvision ? (
          <Text color={colorStyle.color.tertiary}>
            {t('provision:provision.common.permissionGranted')}
          </Text>
        ) : (
          <GrantPermissionAndEnablePolicy
            authenticationInfo={authenticationInfo}
            setExchangeStatus={setExchangeStatus}
            setErrorMsg={setErrorMsg}
            orgId={orgId}
            idSyncMode={idSyncMode}
          />
        ),
    },
  ];

  if (
    idSyncMode === IdSyncMode.Poc &&
    exchangeStatus === ExchangeStatus.WaitSelectUser
  ) {
    stepItems.push({
      icon: <UserIcon />,
      title: (
        <Text color={colorStyle.color.primary}>
          {t('provision:provision.service.exchange.poc.selectUsersDesc', {
            count: exchangePocUserLimit,
          })}
        </Text>
      ),
      description: <PocTransFer ref={pocUsersRef} orgId={orgId} />,
    });
  }

  return (
    <>
      <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('provision.service.exchange.title')}</ModalHeader>
          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex direction="column" gap="16px">
              <Steps direction="vertical" items={stepItems} />

              {exchangeStatus === ExchangeStatus.Provisioning &&
                (syncDataFailFlag ? (
                  <DataSyncFailed
                    service={SERVICE}
                    onRetry={async () => {
                      await ProvisionApi.apiRetrySyncData(SERVICE, orgId);
                      setExchangeStatus(ExchangeStatus.Provisioning);
                      setSyncDataFailFlag(false);
                    }}
                  />
                ) : (
                  <DataSyncing
                    onRefresh={async () => {
                      const res = await ProvisionApi.apiGetProvisionStatus(
                        SERVICE,
                        orgId
                      );
                      if (res.data.status === ProvisionStatusEnum.Provisioned) {
                        setExchangeStatus(ExchangeStatus.Provisioned);
                      }
                    }}
                  />
                ))}
              {exchangeStatus === ExchangeStatus.Provisioned && <DataSynced />}
            </Flex>
          </ModalBody>
          <ModalFooter>
            {exchangeStatus === ExchangeStatus.WaitSelectUser && (
              <LoadingBtn
                variant="primary"
                minWidth="20x"
                onClick={async () => {
                  const selectedCount = pocUsersRef.current.data.length;
                  if (selectedCount === 0) {
                    window.showNotification({
                      severity: 'error',
                      titleMsg: t('errors.PROVISION;POC_SELECT_NO_USER'),
                    });
                    return;
                  }
                  if (selectedCount > exchangePocUserLimit) {
                    window.showNotification({
                      severity: 'error',
                      titleMsg: t('errors.PROVISION;POC_EXCEED_USER_LIMIT', {
                        limit: exchangePocUserLimit,
                      }),
                    });
                    return;
                  }
                  await PocApi.apiTriggerPocIdSync(
                    pocUsersRef.current.data,
                    orgId
                  );
                  console.log(pocUsersRef.current.data);
                  setExchangeStatus(ExchangeStatus.Provisioning);
                }}
              >
                {t('common.action.label.submit')}
              </LoadingBtn>
            )}

            <Space width="2x" />

            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ExchangeOnlineModal;

const IdSyncModeMenu = ({
  disabled,
  idSyncMode,
  setIdSyncMode,
}: {
  disabled: boolean;
  idSyncMode: IdSyncMode;
  setIdSyncMode: (v: IdSyncMode) => void;
}) => {
  const { t } = useTranslation('provision');
  return (
    <Menu top="8px">
      <MenuButton variant="secondary" disabled={disabled}>
        <Text>
          {t(
            `provision:provision.service.exchange.poc.idSyncMode.${idSyncMode}`
          )}
        </Text>
      </MenuButton>
      <MenuList
        width="max-content"
        PopperProps={{ usePortal: true }}
        zIndex="1500"
      >
        {Object.values(IdSyncMode).map((k) => (
          <MenuItem key={k} onClick={() => setIdSyncMode(k as IdSyncMode)}>
            {t(`provision:provision.service.exchange.poc.idSyncMode.${k}`)}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

const GrantPermissionAndEnablePolicy = ({
  authenticationInfo,
  setExchangeStatus,
  setErrorMsg,
  orgId,
  idSyncMode,
}: {
  authenticationInfo: AuthenticationInfoRes | null;
  setExchangeStatus: (status: ExchangeStatus) => void;
  setErrorMsg: (msg: string | undefined) => void;
  orgId: string;
  idSyncMode: IdSyncMode;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation('provision');
  const [loading, toggleLoading] = useToggle(false);
  const radioGroupRef = useRef<{
    enabledPolicyType: EnabledPolicyTypeEnum;
  }>({
    enabledPolicyType: EnabledPolicyTypeEnum.Default,
  });
  const timeRef = useRef<any>(null);
  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: 'exchange',
    });
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const exchangeProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_ex'
      );
      if (exchangeProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(exchangeProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.Exchange,
              code,
              radioGroupRef.current.enabledPolicyType,
              orgId,
              idSyncMode === IdSyncMode.Poc
            );
            if (res.data?.status === 'failure') {
              // failure data = {
              //     "status": "failure",
              //     "reason": "FAILURE;451"
              // }
              setErrorMsg(
                t([
                  `provision.service.exchange.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              const nextStatus =
                idSyncMode === IdSyncMode.Full
                  ? ExchangeStatus.Provisioning
                  : ExchangeStatus.WaitSelectUser;
              setExchangeStatus(nextStatus);
            }
          } catch (e: any) {
            const errorDetail = e?.data?.error?.details?.[0];
            if (errorDetail?.target === 'users' && errorDetail?.limit) {
              setErrorMsg(
                t('errors.PROVISION;POC_EXCEED_USER_LIMIT', {
                  limit: errorDetail.limit,
                })
              );
            } else {
              setErrorMsg(t('common.apiException.internalError'));
            }
          }
        } else {
          setErrorMsg(t('provision.service.exchange.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Flex direction="column" gap="16px" color={colorStyle.color.primary}>
        <Divider variant="dashed" orientation="horizontal" />
        <Text color={colorStyle.color.secondary}>
          {t('provision.common.selectPolicyToEnable')}
        </Text>
        <EnablePolicyRadioGroup
          service={Service.Exchange}
          ref={radioGroupRef}
        />
        <Divider variant="dashed" orientation="horizontal" />
        <Box>
          <Button variant="secondary" borderRadius="2rem" onClick={onClick}>
            {t('provision.button.grantPermission')}
          </Button>
        </Box>
      </Flex>
    </>
  );
};
